import {
  UTCToFormattedPartnerTimeZone,
  UTCToPartnerTimeZone,
  cookieDecryption,
  generateCustomAvatar,
  getDurationDate,
} from '../../../utils';
import { home_noti_reminder_icon } from '../../../utils/images';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import store from '../../../rematch';
import { Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatNotificationFeed, NOTIFICATION_ICON_REMINDER } from './notificationFeedItem';

/**
 * IMP-1937 — the bell dropdown, reading the Notification Feed v2 REST endpoint.
 *
 * Previously this emitted the socket `getNotifications` event, which served the
 * legacy Notification collection. It now dispatches notificationsModel, the same
 * model the header badge reads, so the count and this list come from one source.
 *
 * Row shape and copy live in notificationFeedItem.js; this file is layout,
 * pagination and navigation only.
 */
/** Pixels from the bottom of the tray at which the next page is requested. */
const SCROLL_BOTTOM_THRESHOLD = 24;

const NotificationPopup = ({ popoverRef }) => {
  const { dispatch } = store;
  const navigate = useNavigate();
  const userDetails = cookieDecryption('userDetails');

  // Field by field rather than the whole slice: the badge's unread count lives
  // in this model too, so selecting the slice would re-render the dropdown on
  // every count refresh.
  const feed = useSelector(state => state.notificationsModel.feed);
  const feedPage = useSelector(state => state.notificationsModel.feedPage);
  const feedPages = useSelector(state => state.notificationsModel.feedPages);
  const isFeedLoading = useSelector(state => state.notificationsModel.isFeedLoading);
  const isFeedLoadingMore = useSelector(state => state.notificationsModel.isFeedLoadingMore);
  const feedError = useSelector(state => state.notificationsModel.feedError);
  const unreadCount = useSelector(state => state.notificationsModel.unreadCount);

  // The id whose RSVP is in flight. The tray stays open for the whole request,
  // so without this a second Accept/Decline could fire while the first is out.
  const [pendingRsvpId, setPendingRsvpId] = useState(null);

  useEffect(() => {
    dispatch.notificationsModel.openFeed();
    dispatch.notificationsModel.getNotifications({ page: 1 });

    return () => {
      dispatch.notificationsModel.resetFeed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => formatNotificationFeed(feed, { role: userDetails?.role }), [feed, userDetails?.role]);

  const canMarkAll = unreadCount > 0 || rows.some(row => !row.isRead);

  const handleMarkRead = () => {
    if (!canMarkAll) {
      return;
    }

    dispatch.notificationsModel.markAllAsRead();
  };

  /**
   * Closes the tray and goes to the row's destination. Shared by a plain row
   * click and by answering an event invite.
   *
   */
  const openRow = row => {
    if (!row.isRead) {
      dispatch.notificationsModel.markAsRead(row.id);
    }

    // The trigger toggles, so clicking it when the tray is already closed would
    // reopen it -- which is what happened when an awaited RSVP resolved after
    // the user had closed the tray themselves. Radix keeps the trigger's
    // data-state in sync, so this makes closing idempotent.
    if (popoverRef.current?.dataset?.state === 'open') {
      popoverRef.current.click();
    }

    navigate(row.route, row.routeState ? { state: row.routeState } : undefined);
  };

  /**
   * Sends the RSVP, then goes to Events. The row is left in the tray: the
   * socket implementation dropped it once answered, which only hid it until the
   * next open (the dismissal was component state) while making the tray
   * disagree with the feed it renders. The row still showing both actions is a
   * separate, known gap -- the guest's RSVP is not part of the notification
   * payload.
   *
   * Awaiting the approval matters: navigating first lets the Events page fetch
   * its list before the RSVP is persisted, so the event would render as still
   * unanswered. The effect swallows its own errors, so this resolves either way
   * -- on failure the user still lands on Events, which shows the real state.
   */
  const handleEventInvited = async (row, isApproved) => {
    if (pendingRsvpId) {
      return;
    }

    setPendingRsvpId(row.id);

    try {
      await dispatch.eventModel.eventGuestApproval({
        eventId: row.event?.eventId,
        isApproved,
      });

      openRow(row);
    } finally {
      setPendingRsvpId(null);
    }
  };

  /**
   * Retries the page that failed, not always page 1: if rows are on screen then
   * page 1 succeeded and it was a load-more that broke, so asking for page 1
   * again would discard everything the user had already scrolled through.
   */
  const handleRetry = () => {
    dispatch.notificationsModel.getNotifications({ page: rows.length > 0 ? feedPage + 1 : 1 });
  };

  const handleScrollElement = e => {
    const element = e?.target;

    if (!element) {
      return;
    }

    // Distance, not equality. The socket version tested
    // `scrollHeight === Math.ceil(scrollTop) + clientHeight`, which only fires
    // when the three land on exactly the same integer -- browsers report
    // fractional scrollTop/clientHeight under zoom and on HiDPI displays, so
    // that sum can straddle scrollHeight and never match. The threshold also
    // starts the next page slightly before the true bottom.
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;

    // Guarding on the in-flight flags matters here: a fast scroll fires this
    // handler repeatedly near the bottom, and the socket version had no guard,
    // so it could request the same page twice.
    if (distanceFromBottom > SCROLL_BOTTOM_THRESHOLD || isFeedLoading || isFeedLoadingMore || feedPages <= feedPage) {
      return;
    }

    dispatch.notificationsModel.getNotifications({ page: feedPage + 1 });
  };

  const renderIcon = row => {
    if (row.iconType === NOTIFICATION_ICON_REMINDER) {
      return (
        <div className="reminder_noti_icon">
          <img
            src={home_noti_reminder_icon}
            alt="Reminder"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = process.env.REACT_APP_DEFAULT_THUMBNAIL_URL;
            }}
          />
        </div>
      );
    }

    return (
      <img
        src={row.avatarUrl ? row.avatarUrl : generateCustomAvatar(`${row.avatarFirstName}`, `${row.avatarLastName}`)}
        alt="notification"
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = process.env.REACT_APP_DEFAULT_THUMBNAIL_URL;
        }}
      />
    );
  };

  const renderLoader = () => (
    <div className="home_noti_loader">
      <Loader className="animate-spin mr-2" />
    </div>
  );

  return (
    <>
      <div className="notification_header">
        <div className="notification_txt_link">
          <h6>Notifications</h6>
          {rows.length > 0 && (
            <a
              className={canMarkAll ? 'cursor_pointer' : 'cursor_pointer is_disabled'}
              aria-disabled={!canMarkAll}
              onClick={() => handleMarkRead()}>
              Mark all as read
            </a>
          )}
        </div>
        <div className="notification_scroll custom-scroll" onScroll={handleScrollElement}>
          {/* Data first: a refetch that already has rows must not flash a
              spinner, and an empty first load must not flash "not available". */}
          {rows.length > 0 ? (
            rows.map(row => {
              const elapsed = moment.duration(
                UTCToPartnerTimeZone(new Date()?.toISOString(), userDetails?.timezone).diff(
                  UTCToPartnerTimeZone(row.createdAt, userDetails?.timezone)
                )
              );

              return (
                <div
                  key={row.id}
                  className={`notification_list_box cursor-pointer ${!row.isRead ? '' : 'bg_remove'}`}
                  onClick={() => openRow(row)}>
                  <div className="notification_small_img">{renderIcon(row)}</div>
                  <div className="admin_post_list">
                    <p>
                      <span className="bold_txt">{row.title}</span>
                      <br />
                      <span>{row.body}</span>
                    </p>
                    <p className="min-w-[275px]">
                      {getDurationDate(elapsed, UTCToFormattedPartnerTimeZone(row.createdAt, userDetails?.timezone))}
                      <span className="noti_dot"></span>
                      {UTCToPartnerTimeZone(row.createdAt, userDetails?.timezone).format('MMM DD, yyyy')}
                      {` (${userDetails?.timezone?.abbr})`}
                    </p>

                    {row.subcategory === 'EventInvited' && row.event && (
                      <>
                        <div className="festival_details">
                          <p className="festival_time">
                            {UTCToPartnerTimeZone(row.event.startDate, userDetails?.timezone).format(
                              'ddd, MMM DD, HH:mm'
                            )}
                          </p>
                          <div className="festival_img">
                            <img
                              src={
                                row.event.thumbnail ? row.event.thumbnail : process.env.REACT_APP_DEFAULT_THUMBNAIL_URL
                              }
                              className="w_100"
                              alt="festival"
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = process.env.REACT_APP_DEFAULT_THUMBNAIL_URL;
                              }}
                            />
                          </div>
                          <p className="city_txt">{row.event.name}</p>
                        </div>
                        {/* Both actions are always live. The guest's own RSVP
                              state is not part of the notification payload — it
                              was not in the legacy list projection either — so
                              there is no Accepted/Declined variant to render. */}
                        {!row.event.isEnded && (
                          <div className="invite_btns">
                            <button
                              className="btn danger_btn min"
                              disabled={pendingRsvpId !== null}
                              onClick={event => {
                                event.stopPropagation();
                                handleEventInvited(row, false);
                              }}>
                              Decline
                            </button>
                            <button
                              className="btn btn_primary min"
                              disabled={pendingRsvpId !== null}
                              onClick={event => {
                                event.stopPropagation();
                                handleEventInvited(row, true);
                              }}>
                              Accept
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!row.isRead && (
                    <div className="blue_dot_noti">
                      <span></span>
                    </div>
                  )}
                </div>
              );
            })
          ) : isFeedLoading ? (
            renderLoader()
          ) : feedError ? (
            // Before this branch a failed request fell through to the empty
            // state, so a broken feed told the user they were all caught up.
            <div className="notification_list_box notification_feed_error">
              <p>We couldn&apos;t load your notifications.</p>
              <button type="button" className="btn btn_primary min" onClick={handleRetry}>
                Try again
              </button>
            </div>
          ) : (
            <div className="notification_list_box notification_feed_empty">
              <h6>Nothing new right now</h6>
              <p>We&apos;ll let you know when something needs your attention.</p>
            </div>
          )}

          {isFeedLoadingMore && renderLoader()}

          {/* A failed load-more keeps the rows that did load: dropping them for
              a full-panel error would lose the list the user was reading. */}
          {rows.length > 0 && feedError && !isFeedLoadingMore && (
            <div className="notification_list_box notification_feed_error">
              <p>We couldn&apos;t load more notifications.</p>
              <button type="button" className="btn btn_primary min" onClick={handleRetry}>
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPopup;
